import { type GridColDef, type GridValueGetterParams } from '@mui/x-data-grid'
import { DataTablePaging } from 'components/Shared/DataTablePaging'
import { colorPrimitives } from 'components/Theme'
import { pathPermissions } from 'src/navigation/pathPermissions'
import { formatPhoneNumber } from '@jjvgaming/player-payback-library'
import { useSnackbar } from 'stores/useSnackbar'
import { useState, useEffect } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { ReportExportModal } from '../PlayerPaybackReportExportModal'
import ExportIcon from 'assets/export.svg'
import { useLayout } from 'hooks/useLayout'
import { useGetPatronListByLeCheckins } from 'hooks/api/Reports/useGetPatronListByLeCheckins'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { ErrorIndicator } from 'components/Shared/ErrorIndicator'
import { useSetLe } from 'stores/useSetLe'

interface PatronListTableProps {
  piiPermission: boolean
  downloadPIIPermission: boolean
  paginationModel: any
  onPaginationModelChange: any
  showCorporateAccountRewards: boolean
  showOrgRewards: boolean
  showLeRewards: boolean
  startDate: Date
  endDate: Date
}

export const PatronListTable = ({
  piiPermission,
  downloadPIIPermission,
  paginationModel,
  onPaginationModelChange,
  showCorporateAccountRewards,
  showOrgRewards,
  showLeRewards,
  startDate,
  endDate,
}: PatronListTableProps) => {
  const { passedLe } = useSetLe()
  const setMessage = useSnackbar((state) => state.setMessage)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [columns, setColumns] = useState<GridColDef[]>([])
  const [columnsPII, setColumnsPII] = useState<GridColDef[]>([])
  const toggleModal = () => setIsModalOpen(!isModalOpen)
  const { isMobile } = useLayout()

  // Update query params when `id` changes
  const reportQuery = useGetPatronListByLeCheckins({
    paginationModel,
    currentPage: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    licensedEstablishmentId: Number(passedLe?.id),
    startDate,
    endDate,
  })

  const totalCount = reportQuery.data?.rowCount ?? 0

  useEffect(() => {
    const updatedColumns: GridColDef[] = [
      {
        field: 'patronId',
        headerName: 'Patron ID',
        minWidth: 100,
        flex: 1,
        valueGetter: (params: GridValueGetterParams) =>
          isMobile ? `Patron Id: ${params.row.patronId}` : params.row.patronId,
      },
      {
        field: 'checkinTotal',
        headerName: 'Check-ins',
        minWidth: 140,
        flex: 1,
        valueGetter: (params: GridValueGetterParams) =>
          isMobile
            ? `Check-ins: ${params.row.checkinTotal}`
            : params.row.checkinTotal,
      },
      showLeRewards
        ? {
            field: 'locationPointsTotal',
            headerName: 'Location Points',
            minWidth: 200,
            flex: 1,
            valueGetter: (params: GridValueGetterParams) =>
              isMobile
                ? `Location Points: ${params.row.locationPointsTotal}`
                : params.row.locationPointsTotal,
          }
        : null,
      showOrgRewards
        ? {
            field: 'organizationPointsTotal',
            headerName: 'Organization Points',
            minWidth: 200,
            flex: 1,
            valueGetter: (params: GridValueGetterParams) =>
              isMobile
                ? `Organization Points: ${params.row.organizationPointsTotal}`
                : params.row.organizationPointsTotal,
          }
        : null,
      showCorporateAccountRewards
        ? {
            field: 'corporateAccountPointsTotal',
            headerName: 'Corporate Points',
            minWidth: 200,
            flex: 1,
            valueGetter: (params: GridValueGetterParams) =>
              isMobile
                ? `Corporate Points: ${params.row.corporateAccountPointsTotal}`
                : params.row.corporateAccountPointsTotal,
          }
        : null,
    ].filter(Boolean) as GridColDef[] // filter the null columns that we want to hide from the list

    // PII permission columns
    const updatedColumnsPII: GridColDef[] =
      pathPermissions['/PlayerPaybackReports/PatronList'] &&
      ([
        {
          field: 'patronId',
          headerName: 'Patron ID',
          minWidth: 100,
          flex: 1,
          valueGetter: (params: GridValueGetterParams) =>
            isMobile
              ? `Patron Id: ${params.row.patronId}`
              : params.row.patronId,
        },
        {
          field: 'firstName',
          headerName: 'First Name',
          minWidth: 120,
          flex: 1,
          valueGetter: (params: GridValueGetterParams) =>
            params.row.patron.firstName,
        },
        {
          field: 'lastName',
          headerName: 'Last Name',
          minWidth: 120,
          flex: 1,
          valueGetter: (params: GridValueGetterParams) =>
            params.row.patron.lastName,
        },
        {
          field: 'phoneNumber',
          headerName: 'Phone',
          minWidth: 140,
          flex: 1,
          valueGetter: (params: GridValueGetterParams) =>
            params.row.patron.phoneNumber ?? '',
          valueFormatter: ({ value }: { value: string | undefined }) =>
            formatPhoneNumber(value),
        },
        {
          field: 'email',
          headerName: 'Email',
          minWidth: 300,
          flex: 1,
          valueGetter: (params: GridValueGetterParams) =>
            params.row.patron.email ?? '',
        },
        {
          field: 'addresses',
          minWidth: 400,
          headerName: 'Address',
          flex: 2,
          renderCell: (params: GridValueGetterParams) => {
            return (
              <p className="overflow-hidden whitespace-nowrap text-ellipsis">
                {(params.row.patron.addresses[0]?.address1 &&
                  `${params.row.patron.addresses[0].address1}` +
                    (params.row.patron.addresses[0].address2
                      ? `, ${params.row.patron.addresses[0].address2}`
                      : '')) ??
                  null}
              </p>
            )
          },
          valueGetter: (params: GridValueGetterParams) => {
            return (
              (params.row.patron.addresses[0]?.address1 &&
                `${params.row.patron.addresses[0].address1}` +
                  (params.row.patron.addresses[0].address2
                    ? `, ${params.row.patron.addresses[0].address2}`
                    : '')) ??
              null
            )
          },
        },
        {
          field: 'city',
          headerName: 'City',
          minWidth: 120,
          flex: 1,
          valueGetter: (params: GridValueGetterParams) =>
            params.row.patron.addresses.length
              ? params.row.patron.addresses[0].city
              : '',
        },
        {
          field: 'state',
          headerName: 'State',
          minWidth: 120,
          flex: 1,
          valueGetter: (params: GridValueGetterParams) =>
            params.row.patron.addresses.length
              ? params.row.patron.addresses[0].state
              : '',
        },
        {
          field: 'postalCode',
          headerName: 'Zip',
          minWidth: 120,
          flex: 1,
          valueGetter: (params: GridValueGetterParams) =>
            params.row.patron.addresses.length
              ? params.row.patron.addresses[0].postalCode
              : '',
        },
        {
          field: 'checkinTotal',
          headerName: 'Check-ins',
          minWidth: 140,
          flex: 1,
          valueGetter: (params: GridValueGetterParams) =>
            isMobile
              ? `Check-ins: ${params.row.checkinTotal}`
              : params.row.checkinTotal,
        },
        showLeRewards
          ? {
              field: 'locationPointsTotal',
              headerName: 'Location Points',
              minWidth: 200,
              flex: 1,
              valueGetter: (params: GridValueGetterParams) =>
                isMobile
                  ? `Location Points: ${params.row.locationPointsTotal}`
                  : params.row.locationPointsTotal,
            }
          : null,
        showOrgRewards
          ? {
              field: 'organizationPointsTotal',
              headerName: 'Organization Points',
              minWidth: 200,
              flex: 1,
              valueGetter: (params: GridValueGetterParams) =>
                isMobile
                  ? `Organization Points: ${params.row.organizationPointsTotal}`
                  : params.row.organizationPointsTotal,
            }
          : null,
        showCorporateAccountRewards
          ? {
              field: 'corporateAccountPointsTotal',
              headerName: 'Corporate Points',
              minWidth: 200,
              flex: 1,
              valueGetter: (params: GridValueGetterParams) =>
                isMobile
                  ? `Corporate Points: ${params.row.corporateAccountPointsTotal}`
                  : params.row.corporateAccountPointsTotal,
            }
          : null,
      ].filter(Boolean) as GridColDef[]) // filter the null columns that we want to hide from the list

    // set column values here
    setColumns(updatedColumns)
    setColumnsPII(updatedColumnsPII)
  }, [
    reportQuery.data,
    showCorporateAccountRewards,
    showOrgRewards,
    showLeRewards,
    isMobile,
  ])

  return (
    <Stack spacing={2}>
      {reportQuery.isPending && reportQuery.isFetching && <ActivityIndicator />}
      {reportQuery.isError && <ErrorIndicator />}
      {reportQuery.data && (
        <>
          <Box className="flex lg:justify-end">
            <div hidden={!downloadPIIPermission}>
              <Button variant="contained" onClick={toggleModal}>
                <img
                  src={ExportIcon}
                  width={15}
                  height={15}
                  alt="Export icon"
                />
                <Typography variant="label-CTA">Export</Typography>
              </Button>
            </div>
          </Box>
          <ReportExportModal
            isModalOpen={isModalOpen}
            toggleModal={toggleModal}
            reportName={'Patron List'}
            exportColumns={piiPermission ? columnsPII : columns}
            setMessage={setMessage}
            paginationModel={paginationModel}
            licensedEstablishmentId={Number(passedLe?.id)}
            corporateAccountId={null}
            startDate={startDate}
            endDate={endDate}
            exportType="patronList"
          />
          <DataTablePaging
            getRowId={(row) => row.patronId}
            slotProps={{
              row: {
                style: { cursor: 'default' },
              },
            }}
            noDataMessage="There are no patron accounts available"
            columns={piiPermission ? columnsPII : columns}
            rows={reportQuery.data?.results ?? []}
            totalCount={totalCount}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            sortFieldName="firstName"
            sx={{
              color: colorPrimitives.black,
              '& .MuiDataGrid-row:hover': {
                cursor: 'auto',
              },
            }}
          />
        </>
      )}
    </Stack>
  )
}
